import { useEffect, useState } from "react";
import api, { fmtIDR, fmtDate } from "@/lib/api";
import { PageHeader, Loading, Empty } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Coins } from "lucide-react";

export default function Kasbon() {
  const [rows, setRows] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ staff_id: "", amount: 0, note: "" });
  const [payFor, setPayFor] = useState(null);
  const [payAmt, setPayAmt] = useState(0);

  const load = () => { setLoading(true); api.get("/kasbon").then(({ data }) => setRows(data)).finally(() => setLoading(false)); };
  useEffect(() => { load(); api.get("/staff").then(({ data }) => setStaff(data)).catch(() => {}); }, []);

  const create = async () => {
    if (!form.staff_id || !form.amount) return toast.error("Pilih karyawan & isi nominal");
    try {
      await api.post("/kasbon", { staff_id: form.staff_id, amount: Number(form.amount), note: form.note });
      toast.success("Kasbon tersimpan"); setOpen(false); setForm({ staff_id: "", amount: 0, note: "" }); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const pay = async () => {
    try {
      await api.post(`/kasbon/${payFor.id}/pay`, { amount: Number(payAmt) });
      toast.success("Pembayaran tercatat"); setPayFor(null); setPayAmt(0); load();
    } catch { toast.error("Gagal"); }
  };

  const del = async (r) => { if (!window.confirm("Hapus kasbon?")) return; await api.delete(`/kasbon/${r.id}`); load(); };

  const totalOpen = rows.filter((r) => r.status === "open").reduce((s, r) => s + (r.remaining || 0), 0);

  return (
    <div>
      <PageHeader title="Kasbon Karyawan" subtitle="Pinjaman karyawan & status pelunasan">
        <Button onClick={() => setOpen(true)} className="bg-sky-600 hover:bg-sky-700" data-testid="kasbon-add-btn">
          <Plus className="w-4 h-4 mr-1.5" /> Kasbon Baru
        </Button>
      </PageHeader>

      <div className="mb-4 bg-gradient-to-br from-amber-500 to-amber-600 text-white rounded-xl shadow-sm p-4 inline-flex items-center gap-3">
        <Coins className="w-5 h-5" />
        <span className="text-xs uppercase tracking-wide font-medium opacity-90">Total Kasbon Aktif</span>
        <span className="text-xl font-semibold font-mono-num" data-testid="kasbon-total">{fmtIDR(totalOpen)}</span>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200"><tr>
            {["Tanggal", "Karyawan", "Nominal", "Dibayar", "Sisa", "Status", "Catatan", "Aksi"].map((h) => <th key={h} className="px-4 py-3 text-left font-medium text-slate-500">{h}</th>)}
          </tr></thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? <tr><td colSpan={8}><Loading /></td></tr>
              : rows.length === 0 ? <tr><td colSpan={8}><Empty /></td></tr>
              : rows.map((r) => (
                <tr key={r.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 text-slate-500">{fmtDate(r.date)}</td>
                  <td className="px-4 py-3">{r.staff_name}</td>
                  <td className="px-4 py-3 font-mono-num">{fmtIDR(r.amount)}</td>
                  <td className="px-4 py-3 font-mono-num text-green-600">{fmtIDR(r.paid)}</td>
                  <td className="px-4 py-3 font-mono-num text-amber-600">{fmtIDR(r.remaining)}</td>
                  <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${r.status === "paid" ? "bg-green-100 text-green-700 border-green-200" : "bg-amber-100 text-amber-800 border-amber-200"}`}>{r.status === "paid" ? "Lunas" : "Aktif"}</span></td>
                  <td className="px-4 py-3 text-slate-500 max-w-[160px] truncate">{r.note}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {r.status !== "paid" && <Button size="sm" variant="outline" className="text-xs mr-1" onClick={() => { setPayFor(r); setPayAmt(r.remaining); }} data-testid={`kasbon-pay-${r.id}`}>Bayar</Button>}
                    <button onClick={() => del(r)} className="p-1.5 text-slate-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Kasbon Baru</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs text-slate-500">Karyawan</Label>
              <Select value={form.staff_id} onValueChange={(v) => setForm({ ...form, staff_id: v })}>
                <SelectTrigger className="mt-1" data-testid="kasbon-staff"><SelectValue placeholder="Pilih karyawan" /></SelectTrigger>
                <SelectContent>{staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs text-slate-500">Nominal</Label><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-1" data-testid="kasbon-amount" /></div>
            <div><Label className="text-xs text-slate-500">Catatan</Label><Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="mt-1" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Batal</Button><Button onClick={create} className="bg-sky-600 hover:bg-sky-700" data-testid="kasbon-save-btn">Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!payFor} onOpenChange={(o) => !o && setPayFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Bayar Kasbon — {payFor?.staff_name}</DialogTitle></DialogHeader>
          <div className="py-2">
            <Label className="text-xs text-slate-500">Jumlah Bayar (sisa {fmtIDR(payFor?.remaining)})</Label>
            <Input type="number" value={payAmt} onChange={(e) => setPayAmt(e.target.value)} className="mt-1" data-testid="kasbon-pay-amount" />
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setPayFor(null)}>Batal</Button><Button onClick={pay} className="bg-sky-600 hover:bg-sky-700" data-testid="kasbon-pay-confirm">Bayar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
